import { Resource, epochISOString, toISODate } from 'idea-toolbox';

import { LEGAL_DOCUMENTS_VERSIONS, LegalDocumentsVersions } from './user.model';

/**
 * The legal documents of the platform and of the event; the values are the IDs of the documents (and their paths
 * in the front-end).
 */
export enum LegalDocuments {
  PRIVACY_POLICY = 'privacy-policy',
  TERMS = 'terms',
  EVENT_TERMS = 'event-terms',
  CODE_OF_CONDUCT = 'code-of-conduct'
}

/**
 * For each legal document of the platform, the matching attribute in the versions accepted by the users.
 * The documents of the event aren't here: they are accepted when registering to the event, without a version.
 */
const VERSIONS_KEY_BY_DOCUMENT: { [document in LegalDocuments]?: keyof LegalDocumentsVersions } = {
  [LegalDocuments.PRIVACY_POLICY]: 'privacyPolicy',
  [LegalDocuments.TERMS]: 'termsAndConditions'
};

/**
 * The format of the ID of an uploaded PDF.
 */
const PDF_URI_FORMAT = /^[\w-]+$/;

/**
 * A legal document of the platform, that the admins can edit.
 */
export class LegalDocument extends Resource {
  /**
   * The ID of the document.
   */
  documentId: LegalDocuments;
  /**
   * The content of the document, in HTML.
   * If not set, the admins have yet to edit the document: the front-end shows the default one (in the translations).
   */
  content: string | null;
  /**
   * The ID of the PDF uploaded by the admins, if any; when set, the document is the PDF (instead of the content).
   */
  pdfURI: string | null;
  /**
   * A temporary URL to the uploaded PDF, if any; set by the back-end when the document is requested, never stored.
   */
  pdfURL?: string;
  /**
   * The version of the document; when it changes, the users are asked to accept the documents again.
   * Not set for the documents that aren't accepted when signing-in (see `isAcceptedAtSignIn`).
   */
  version: string | null;
  /**
   * Timestamp of the last change by an admin, if any.
   */
  updatedAt?: epochISOString;

  load(x: any): void {
    super.load(x);
    this.documentId = this.clean(x.documentId, String) as LegalDocuments;
    this.content = this.clean(x.content, String);
    this.pdfURI = this.clean(x.pdfURI, String);
    if (x.pdfURL) this.pdfURL = this.clean(x.pdfURL, String);
    this.version = this.clean(x.version, String, LEGAL_DOCUMENTS_VERSIONS[VERSIONS_KEY_BY_DOCUMENT[this.documentId]]);
    if (x.updatedAt) this.updatedAt = this.clean(x.updatedAt, d => new Date(d).toISOString());
  }

  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.documentId = safeData.documentId;
    delete this.pdfURL;
    this.version = safeData.version;
    this.updatedAt = safeData.updatedAt;
  }

  validate(): string[] {
    const e = super.validate();
    if (!Object.values(LegalDocuments).includes(this.documentId)) e.push('documentId');
    if (this.pdfURI && !PDF_URI_FORMAT.test(this.pdfURI)) e.push('pdfURI');
    // the documents of the platform need either a content or a PDF; the ones of the event default to a bundled PDF
    if (this.isAcceptedAtSignIn() && !this.pdfURI && this.iE(this.content)) e.push('content');
    return e;
  }

  /**
   * Whether it's a document of the platform, accepted by the users (in a specific version) when they sign-in.
   */
  isAcceptedAtSignIn(): boolean {
    return !!VERSIONS_KEY_BY_DOCUMENT[this.documentId];
  }

  /**
   * Set a new version for the document, so that the users are asked to accept it again.
   * The version is the current date; in case of more versions in the same day, a revision number is added.
   */
  bumpVersion(): void {
    if (!this.isAcceptedAtSignIn()) return;
    const today = toISODate(new Date());
    const [date, revision] = this.version.split('.');
    this.version = date === today ? `${today}.${Number(revision ?? 1) + 1}` : today;
  }
}

/**
 * Get the current versions of the legal documents, in the format accepted by the users.
 * The documents missing from the list are considered at their default version.
 */
export const getLegalDocumentsVersions = (documents: LegalDocument[]): LegalDocumentsVersions =>
  documents
    .filter(doc => doc.isAcceptedAtSignIn())
    .reduce(
      (versions, doc): LegalDocumentsVersions => ({
        ...versions,
        [VERSIONS_KEY_BY_DOCUMENT[doc.documentId]]: doc.version
      }),
      { ...LEGAL_DOCUMENTS_VERSIONS }
    );
